"use client"

import { useState, useCallback } from "react"
import { RefreshCw, ArrowRight } from "lucide-react"
import { trackProductEvent } from "@/lib/analytics/client"
import styles from "./choice-tool.module.css"

interface Pair {
  a: string
  b: string
  category: string
}

const ALL_PAIRS: Pair[] = [
  // Food & Drink
  { a: "Coffee", b: "Tea", category: "food" },
  { a: "Pizza", b: "Burgers", category: "food" },
  { a: "Chocolate", b: "Vanilla", category: "food" },
  { a: "Pancakes", b: "Waffles", category: "food" },
  { a: "Ice cream", b: "Cake", category: "food" },
  { a: "Sushi", b: "Tacos", category: "food" },
  { a: "Sweet", b: "Savory", category: "food" },
  { a: "Breakfast for dinner", b: "Dinner for breakfast", category: "food" },
  { a: "Fries", b: "Onion rings", category: "food" },
  { a: "Hot chocolate", b: "Apple cider", category: "food" },
  { a: "Pasta", b: "Rice", category: "food" },
  { a: "Smoothie", b: "Milkshake", category: "food" },

  // Entertainment
  { a: "Movies", b: "TV shows", category: "entertainment" },
  { a: "Books", b: "Podcasts", category: "entertainment" },
  { a: "Netflix", b: "YouTube", category: "entertainment" },
  { a: "Comedy", b: "Horror", category: "entertainment" },
  { a: "Marvel", b: "DC", category: "entertainment" },
  { a: "Board games", b: "Video games", category: "entertainment" },
  { a: "Concerts", b: "Festivals", category: "entertainment" },
  { a: "Anime", b: "Cartoons", category: "entertainment" },
  { a: "Spotify", b: "Apple Music", category: "entertainment" },
  { a: "Action movies", b: "Romance movies", category: "entertainment" },

  // Lifestyle
  { a: "Early bird", b: "Night owl", category: "lifestyle" },
  { a: "City life", b: "Country life", category: "lifestyle" },
  { a: "Summer", b: "Winter", category: "lifestyle" },
  { a: "Dogs", b: "Cats", category: "lifestyle" },
  { a: "Bath", b: "Shower", category: "lifestyle" },
  { a: "Minimalism", b: "Maximalism", category: "lifestyle" },
  { a: "Working out", b: "Sleeping in", category: "lifestyle" },
  { a: "Text message", b: "Phone call", category: "lifestyle" },
  { a: "Online shopping", b: "In-store shopping", category: "lifestyle" },
  { a: "Window seat", b: "Aisle seat", category: "lifestyle" },
  { a: "Sneakers", b: "Sandals", category: "lifestyle" },
  { a: "Long hair", b: "Short hair", category: "lifestyle" },

  // Travel
  { a: "Beach vacation", b: "Mountain trip", category: "travel" },
  { a: "Road trip", b: "Flight", category: "travel" },
  { a: "Camping", b: "Hotel", category: "travel" },
  { a: "Europe", b: "Asia", category: "travel" },
  { a: "Solo travel", b: "Group travel", category: "travel" },
  { a: "Backpacking", b: "Luxury resort", category: "travel" },
  { a: "Tropical island", b: "Historic city", category: "travel" },
  { a: "Cruise", b: "Safari", category: "travel" },

  // Hypothetical
  { a: "Read minds", b: "Be invisible", category: "hypothetical" },
  { a: "Time travel to the past", b: "Time travel to the future", category: "hypothetical" },
  { a: "Be famous", b: "Be wealthy", category: "hypothetical" },
  { a: "Live forever", b: "Live life to the fullest", category: "hypothetical" },
  { a: "Fly", b: "Teleport", category: "hypothetical" },
  { a: "Super strength", b: "Super speed", category: "hypothetical" },
  { a: "Know everything", b: "Experience everything", category: "hypothetical" },
  { a: "Never sleep", b: "Never eat", category: "hypothetical" },

  // Social
  { a: "Big party", b: "Small gathering", category: "social" },
  { a: "Texting", b: "Face-to-face", category: "social" },
  { a: "Instagram", b: "TikTok", category: "social" },
  { a: "Give gifts", b: "Receive gifts", category: "social" },
  { a: "Lead the group", b: "Go with the flow", category: "social" },
  { a: "Talk it out", b: "Write it out", category: "social" },
  { a: "Game night", b: "Movie night", category: "social" },
  { a: "New friends", b: "Old friends", category: "social" },
]

export function ThisOrThatGame() {
  const [index, setIndex] = useState(0)
  const [picked, setPicked] = useState<"a" | "b" | null>(null)
  const [count, setCount] = useState(0)

  const pair = index < ALL_PAIRS.length ? ALL_PAIRS[index] : null

  const handlePick = useCallback(
    (choice: "a" | "b") => {
      if (picked) return
      if (count === 0) {
        trackProductEvent("tool_started", {
          properties: { tool_slug: "this-or-that" },
        })
      }
      trackProductEvent("tool_completed", {
        properties: {
          tool_slug: "this-or-that",
          interaction_type: "choice",
          choice,
          category: pair?.category ?? "unknown",
          question_index: index,
          interactions_completed: count + 1,
        },
      })
      setPicked(choice)
      setCount((c) => c + 1)
    },
    [count, index, pair?.category, picked]
  )

  const next = useCallback(() => {
    setPicked(null)
    setIndex((i) => i + 1)
  }, [])

  const reset = useCallback(() => {
    setIndex(0)
    setPicked(null)
    setCount(0)
  }, [])

  if (!pair) {
    return (
      <div className={styles.tool}>
        <div className={styles.result}>
          <p>
            <strong>All done.</strong> You answered {count} quick choices.
          </p>
          <button onClick={reset} className={styles.next}>
            <RefreshCw aria-hidden="true" /> Play again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tool}>
      <div className={styles.topbar}>
        <p className={styles.eyebrow}>This or that?</p>
        <span className={styles.counter}>
          {index + 1} / {ALL_PAIRS.length}
        </span>
      </div>

      <div className={styles.choices}>
        {(["a", "b"] as const).map((side) => {
          const label = pair[side]
          const isPicked = picked === side
          const isOther = picked && picked !== side

          return (
            <button
              key={side}
              onClick={() => handlePick(side)}
              disabled={!!picked}
              className={`${styles.choice} ${
                side === "a" ? styles.choiceA : styles.choiceB
              } ${isPicked ? styles.selected : isOther ? styles.muted : ""}`}
            >
              <span className={styles.choiceLabel}>Option {side.toUpperCase()}</span>
              <span className={styles.choiceText}>{label}</span>
              <span className={styles.choiceHint}>
                {isPicked ? "Your choice" : "Tap to choose"}
              </span>
            </button>
          )
        })}
      </div>

      {picked && (
        <div className={styles.result} aria-live="polite">
          <p>
            <strong>{count} answered.</strong> Keep moving or compare with a friend.
          </p>
          <button onClick={next} className={styles.next}>
            Next question <ArrowRight aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}
